import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Archive,
  CircleDot,
  Coins,
  CornerDownRight,
  Percent,
  PlusCircle,
  Type,
} from "lucide-react";
import { DynamicIcon } from "lucide-react/dynamic";
import { Suspense, useMemo, useState } from "react";
import type { BudgetPermissionType } from "schema";
import { capitalize } from "shared";
import { twMerge } from "tailwind-merge";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import type { FiltersState } from "@/components/data-table-filter/core/types";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { SpendProgressBar } from "@/components/spend-progress-bar";
import {
  DashedOutCell,
  RenderCell,
} from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BasicTooltip } from "@/components/ui/tooltip";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { type RouterOutputs, trpc } from "@/lib/trpcClient";
import { AccessListing } from "./budgets/-components/access-listing";
import { BulkAccessEdit } from "./budgets/-components/bulk-access-edit";

type BudgetType = RouterOutputs["budget"]["list"][number];
export type RowBudgetModel = {
  name: string;
  description?: string | null;
  balanceCents: number;
  targetCents: number;
  status: BudgetType["status"] | null;
  refreshInterval: BudgetType["refreshInterval"];
  id: string;
  budgetGroupId?: string;
  subBudgets: RowBudgetModel[] | null;
  budgetPermissions?: BudgetPermissionType[];
};

const dtf = createColumnConfigHelper<RowBudgetModel>();
const columnsConfig = [
  dtf
    .text()
    .id("Name")
    .accessor((row) => row.name)
    .displayName("Name")
    .icon(Type)
    .build(),
  dtf
    .option()
    .id("Status")
    .accessor((row) => row.status)
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .options([
      { label: "Active", value: "active" },
      { label: "Archived", value: "archived" },
    ])
    .displayName("Status")
    .icon(CircleDot)
    .hide()
    .build(),
  dtf
    .number()
    .id("Balance")
    .accessor((row) => row.balanceCents / 100)
    .displayName("Balance")
    .icon(Coins)
    .build(),
  dtf
    .number()
    .id("Utilization")
    .accessor((row) =>
      row.targetCents > 0
        ? Math.round((row.balanceCents / row.targetCents) * 100)
        : 0,
    )
    .displayName("Utilization")
    .icon(Percent)
    .build(),
];

export const Route = createFileRoute("/_authed/_org/budgets")({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.budget.list.queryOptions());
  },
});

const initialFilterState: FiltersState = [
  {
    columnId: "Status",
    hide: true,
    operator: "is",
    type: "option",
    values: ["active"],
  },
];

type View = "active" | "frozen" | "archived";

function getInitialViewFromFilters(filters: FiltersState): View {
  const budgetStatus = filters.find((filter) => filter.columnId === "Status");
  if (!budgetStatus) return "active";

  return budgetStatus.values[0] as View;
}

function RouteComponent() {
  const navigate = Route.useNavigate();
  const location = useLocation();
  const { admin } = useRequiredRole();
  const { data: fetchData, isLoading } = useQuery(
    trpc.budget.list.queryOptions(),
  );
  const budgets = fetchData ?? [];

  // useMemo is important because Tanstack Table expects a stable reference
  const data: RowBudgetModel[] = useMemo(
    () =>
      budgets.map((b) => {
        const subBudgets =
          b.subBudgets.length > 1
            ? b.subBudgets.map((sb) => ({
                ...sb,
                refreshInterval: b.refreshInterval,
                status: b.status,
                subBudgets: null,
              }))
            : [];

        return {
          ...b,
          balanceCents: b.subBudgets.reduce(
            (acc, sb) => acc + sb.balanceCents,
            0,
          ),
          status: b.status,
          subBudgets,
          targetCents: b.subBudgets.reduce(
            (acc, sb) => acc + sb.targetCents,
            0,
          ),
        };
      }),
    [budgets],
  );

  const tableColumns: ColumnDef<RowBudgetModel>[] = [
    {
      accessorFn: (row) => row.name,
      cell: ({ row }) => (
        <div className="flex items-center justify-between">
          <div className="flex">
            {!row.original.subBudgets && (
              <CornerDownRight
                className={twMerge(
                  "mt-0.5 mr-1.5 ml-3 size-3.5 text-neutral-400",
                )}
              />
            )}

            <div className="flex items-center gap-1 truncate">
              {row.subRows.length > 0 &&
                (row.original.subBudgets?.length ?? 0) > 1 && (
                  <BasicTooltip
                    tooltip={
                      row.getIsExpanded()
                        ? "Collapse sub-budgets"
                        : "Expand sub-budgets"
                    }
                  >
                    <button
                      type="button"
                      className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-neutral-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                      }}
                    >
                      <DynamicIcon
                        name={
                          row.getIsExpanded() ? "chevron-down" : "chevron-right"
                        }
                        strokeWidth={2}
                        className="h-3.5 w-3.5 text-neutral-500"
                      />
                    </button>
                  </BasicTooltip>
                )}
              {row.original.name}
            </div>
          </div>
        </div>
      ),
      id: "Name",
    },
    {
      accessorFn: (row) => row.balanceCents / 100,
      cell: (cell) => (
        <SpendProgressBar
          spendLimit={{
            ...cell.row.original,
            refreshInterval: cell.row.original.subBudgets
              ? (cell.row.original.refreshInterval ?? undefined)
              : undefined,
          }}
        />
      ),
      id: "Balance",
      minSize: 300,
    },
    {
      accessorFn: (row) =>
        row.targetCents > 0
          ? Math.round((row.balanceCents / row.targetCents) * 100)
          : 0,
      cell: ({ row }) => {
        const utilization =
          row.original.targetCents > 0
            ? Math.round(
                (row.original.balanceCents / row.original.targetCents) * 100,
              )
            : 0;
        return <div className="text-right font-medium">{utilization}%</div>;
      },
      id: "Utilization",
      size: 100,
    },
    {
      accessorFn: (row) => row.status,
      cell: ({ row }) => {
        return row.original.budgetGroupId ? (
          <DashedOutCell />
        ) : (
          <RenderCell status={row.original.status} type="status" />
        );
      },
      id: "Status",
    },
    {
      accessorFn: (row) => row.budgetPermissions?.length ?? 0,
      cell: ({ row }) => {
        if (!row.original.budgetPermissions) return <DashedOutCell />;
        if (row.original.budgetPermissions.length === 0)
          return <span className="text-muted-foreground">None</span>;

        return (
          <AccessListing
            users={row.original.budgetPermissions.map((bp) => bp.userId)}
          />
        );
      },
      id: "Access",
    },
  ];
  const tableName = "budgets";

  const { table, filters, actions, columns, strategy, setTableFilters } =
    usePersistedReactTable({
      columns: tableColumns,
      data,
      filterFromLeafRows: true,
      filterOptions: {
        columnsConfig,
        defaultFilters: initialFilterState,
        strategy: "client",
      },
      getRowId: (row) => row.id.toString(),
      getSubRows: (row) => {
        return row.subBudgets ?? [];
      },
      tableName,
    });

  const activeBudgetsCount = isLoading
    ? undefined
    : data.filter((budget) => budget.status === "active").length;

  const archivedBudgetsCount = isLoading
    ? undefined
    : data.filter((budget) => budget.status === "archived").length;

  const [view, setView] = useState<View>(getInitialViewFromFilters(filters));

  const updateStatusFilter = (status: View) => {
    setView(status);
    setTableFilters((filters) =>
      filters.map((f) =>
        f.columnId === "Status"
          ? {
              ...f,
              values: [status],
            }
          : f,
      ),
    );
  };

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/budgets",
          });
        }
      }}
      open={
        location.pathname !== "/budgets" &&
        location.pathname.includes("/budgets")
      }
    >
      <Page
        heading="Budgets"
        actions={
          admin && (
            <Link to="/budgets/create" className="ml-auto">
              <Button className="ml-2">
                <PlusCircle />
                Create Budget
              </Button>
            </Link>
          )
        }
        table={{
          actions,
          columns,
          filters,
          strategy,
          table,
        }}
        tabs={
          <Tabs
            onValueChange={(v) => {
              updateStatusFilter(v as View);
            }}
            value={view}
          >
            <TabsList variant="standard">
              <TabsTrigger
                value="active"
                variant="standard"
                count={activeBudgetsCount}
              >
                <span className="flex items-center gap-2">
                  <CircleDot className="size-3.5 text-neutral-400" />
                  Active
                </span>
              </TabsTrigger>

              <TabsTrigger
                value="archived"
                variant="standard"
                count={archivedBudgetsCount}
              >
                <span className="flex items-center gap-2">
                  <Archive className="size-3.5 text-neutral-400" />
                  Archived
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
      >
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">No Matching Budgets</span>

              <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                Clear Filters
              </Button>
            </div>
          }
          table={table}
          tableName={tableName}
          navOptions={(row) =>
            linkOptions({
              params: {
                groupId: row.original.budgetGroupId ?? row.original.id,
              },
              to: "/budgets/$groupId",
            })
          }
          loading={isLoading}
          className="[&_th]:border-t-0!"
          selectable
        />

        <TablePagination
          tableName={tableName}
          table={table}
          leftSlot={
            table.getFilteredSelectedRowModel().rows.length > 0 && (
              <BulkAccessEdit
                budgets={table
                  .getFilteredSelectedRowModel()
                  .rows.map((r) => r.original)}
              />
            )
          }
        />
      </Page>

      <SheetContent
        className="w-full"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <Suspense
          fallback={
            <div className="px-6">
              <SheetSkeleton />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </SheetContent>
    </Sheet>
  );
}
